import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Bell, Check, Clock, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export const NotificationHistory = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all', 'unread', 'read'

  useEffect(() => {
    fetchNotifications();
  }, [user]);

  const fetchNotifications = async () => {
    if (!user || !localStorage.getItem('token')) return;
    
    setLoading(true);
    try {
      let endpoint = '';
      // Skip for employees - not implemented yet
      if (user.role === 'admin' || user.role === 'stationsleitung') {
        endpoint = `${API}/api/admin/notifications`;
      } else if (user.role === 'superadmin') {
        endpoint = `${API}/api/superadmin/notifications`;
      }
      
      if (endpoint) {
        const response = await axios.get(endpoint, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        setNotifications(response.data || []);
      } else {
        // No notifications for employees yet
        setNotifications([]);
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationClick = async (notification) => {
    try {
      // Mark as read if unread (skip for employees - not implemented)
      if (!notification.is_read) {
        let endpoint = '';
        if (user.role === 'admin' || user.role === 'stationsleitung') {
          endpoint = `${API}/api/admin/notifications/${notification.id}/read`;
        } else if (user.role === 'superadmin') {
          endpoint = `${API}/api/superadmin/notifications/${notification.id}/read`;
        }
        
        if (endpoint) {
          await axios.put(endpoint, {}, { 
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } 
          });
          
          // Update local state
          setNotifications(prev => 
            prev.map(n => n.id === notification.id ? { ...n, is_read: true } : n)
          );
        }
      }
      
      // Navigate based on notification type
      const type = notification.type || '';
      
      if (type === 'instruction_assigned' || type === 'instruction_reminder') {
        navigate('/employee/unterweisungen');
      } else if (
        type === 'improvement_suggestion' || 
        type === 'improvement_suggestion_forwarded' ||
        type === 'suggestion_reply' ||
        type === 'suggestion_status_update' ||
        type === 'suggestion_archived' ||
        type === 'suggestion_created' ||
        type.includes('suggestion')
      ) {
        if (user.role === 'superadmin') {
          navigate('/superadmin/notifications');
        } else if (user.role === 'admin' || user.role === 'stationsleitung') {
          navigate('/admin/improvement-suggestions');
        } else if (user.role === 'employee') {
          navigate('/employee/verbesserungen');
        }
      } else if (type === 'location_updated' || type === 'location_created' || type === 'location_deleted' || type.includes('location')) {
        if (user.role === 'superadmin') {
          navigate('/superadmin/locations');
        }
      } else if (type.includes('employee')) {
        if (user.role === 'superadmin' || user.role === 'admin') {
          navigate('/admin/employees');
        }
      } else if (type === 'new_order' || type.includes('order')) {
        if (user.role === 'admin' || user.role === 'stationsleitung') {
          navigate('/admin/bestellungen');
        }
      }
    } catch (error) {
      console.error('Error handling notification:', error);
    }
  };

  const filteredNotifications = notifications.filter(n => {
    if (filter === 'unread') return !n.is_read;
    if (filter === 'read') return n.is_read;
    return true;
  });

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Gerade eben';
    if (diffMins < 60) return `vor ${diffMins} Min.`;
    if (diffHours < 24) return `vor ${diffHours} Std.`;
    if (diffDays < 7) return `vor ${diffDays} Tag${diffDays > 1 ? 'en' : ''}`;
    
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <DashboardLayout>
      <div className="p-4 sm:p-6 max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <Bell className="h-8 w-8 text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Benachrichtigungen</h1>
          </div>
          <p className="text-gray-600 dark:text-gray-400">Alle Ihre Benachrichtigungen im Überblick</p>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 mb-6 border-b border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 font-medium transition-colors ${
              filter === 'all'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Alle ({notifications.length})
          </button>
          <button
            onClick={() => setFilter('unread')}
            className={`px-4 py-2 font-medium transition-colors ${
              filter === 'unread'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Ungelesen ({notifications.filter(n => !n.is_read).length})
          </button>
          <button
            onClick={() => setFilter('read')}
            className={`px-4 py-2 font-medium transition-colors ${
              filter === 'read'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Gelesen ({notifications.filter(n => n.is_read).length})
          </button>
        </div>

        {/* Notifications List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-500 mt-4">Laden...</p>
          </div>
        ) : filteredNotifications.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <Bell className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">
              {filter === 'unread' ? 'Keine ungelesenen Benachrichtigungen' : 
               filter === 'read' ? 'Keine gelesenen Benachrichtigungen' : 
               'Keine Benachrichtigungen'}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredNotifications.map((notification) => (
              <div
                key={notification.id}
                onClick={() => handleNotificationClick(notification)}
                className={`p-4 rounded-lg border cursor-pointer transition-all hover:shadow-md ${
                  notification.is_read
                    ? 'bg-white border-gray-200'
                    : 'bg-blue-50 border-blue-200'
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      {!notification.is_read && (
                        <div className="w-2 h-2 bg-blue-600 rounded-full flex-shrink-0"></div>
                      )}
                      <p className={`text-sm font-medium ${
                        notification.is_read ? 'text-gray-900' : 'text-gray-900'
                      }`}>
                        {notification.message}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <Clock className="h-3 w-3" />
                      <span>{formatDate(notification.created_at)}</span>
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-gray-400 flex-shrink-0" />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};
